import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { Button } from "../components/ui/button";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { toast } from "sonner";
import { 
  CheckCircle2, 
  Clock, 
  AlertCircle, 
  Pause, 
  RefreshCw,
  Plus
} from "lucide-react";
import axios from "axios";
import TaskFormModal from "../components/TaskFormModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLUMNS = [
  { id: "TODO", title: "Da Fare", icon: AlertCircle, color: "text-zinc-400" },
  { id: "WIP", title: "In Corso", icon: Clock, color: "text-amber-500" },
  { id: "ONLINE", title: "Completati", icon: CheckCircle2, color: "text-emerald-500" },
  { id: "stand-by", title: "Stand-by", icon: Pause, color: "text-purple-400" },
  { id: "Recurring", title: "Ricorrenti", icon: RefreshCw, color: "text-indigo-400" }
];

const PRIORITY_BORDER = {
  HIGH: "border-l-red-500",
  MEDIUM: "border-l-amber-500",
  LOW: "border-l-blue-500"
};

export default function KanbanBoard() {
  const [tasks, setTasks] = useState([]);
  const [options, setOptions] = useState({ systems: [], countries: [], priorities: [], statuses: [], projects: [] });
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [tasksRes, optionsRes] = await Promise.all([
        axios.get(`${API}/tasks`),
        axios.get(`${API}/options`)
      ]);
      setTasks(tasksRes.data);
      setOptions(optionsRes.data);
    } catch (error) {
      console.error("Error fetching tasks:", error);
      toast.error("Errore nel caricamento");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getColumnTasks = (columnId) => {
    if (columnId === "Recurring") {
      return tasks.filter(t => t.status?.includes("Recurring"));
    }
    return tasks.filter(t => t.status === columnId && !t.status?.includes("Recurring"));
  };

  const onDragEnd = async (result) => {
    const { destination, source, draggableId } = result;

    if (!destination) return;
    if (destination.droppableId === source.droppableId && destination.index === source.index) return;

    const newStatus = destination.droppableId;
    const task = tasks.find(t => t.id === draggableId);
    
    if (!task) return;

    // Optimistic update
    setTasks(prev => prev.map(t => 
      t.id === draggableId ? { ...t, status: newStatus } : t
    ));

    try {
      await axios.put(`${API}/tasks/${draggableId}`, { status: newStatus });
      toast.success(`Task spostato in "${COLUMNS.find(c => c.id === newStatus)?.title}"`);
    } catch (error) {
      // Revert on error
      setTasks(prev => prev.map(t => 
        t.id === draggableId ? { ...t, status: task.status } : t
      ));
      toast.error("Errore nell'aggiornamento");
    }
  };

  const handleTaskClick = (task) => {
    setEditingTask(task);
    setModalOpen(true);
  };

  const handleSave = async (taskData) => {
    try {
      if (editingTask) {
        await axios.put(`${API}/tasks/${editingTask.id}`, taskData);
        toast.success("Task aggiornato");
      } else {
        await axios.post(`${API}/tasks`, taskData);
        toast.success("Task creato");
      }
      setModalOpen(false);
      fetchData();
    } catch (error) {
      toast.error("Errore durante il salvataggio");
    }
  };

  const handleCreate = (status) => {
    setEditingTask({ status });
    setModalOpen(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 h-full flex flex-col" data-testid="kanban-board">
      <div className="mb-4 md:mb-6">
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Kanban Board</h1>
        <p className="text-sm md:text-base text-muted-foreground mt-1">Trascina i task per cambiare lo status</p>
      </div>

      <DragDropContext onDragEnd={onDragEnd}>
        {/* Desktop: Horizontal scroll */}
        <div className="hidden md:flex gap-4 overflow-x-auto pb-4 flex-1 snap-x">
          {COLUMNS.map((column) => {
            const columnTasks = getColumnTasks(column.id);
            const Icon = column.icon;

            return (
              <div 
                key={column.id} 
                className="flex-shrink-0 w-72 snap-start"
                data-testid={`kanban-column-${column.id}`}
              >
                <Card className="border-border h-full flex flex-col">
                  <CardHeader className="pb-3 flex-shrink-0">
                    <CardTitle className="text-base flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Icon className={`w-4 h-4 ${column.color}`} />
                        <span>{column.title}</span>
                      </div>
                      <Badge variant="secondary" className="text-xs">
                        {columnTasks.length}
                      </Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="flex-1 overflow-hidden p-2">
                    <Droppable droppableId={column.id}>
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                          className={`h-full min-h-[200px] overflow-y-auto space-y-2 p-1 rounded-lg transition-colors ${
                            snapshot.isDraggingOver ? 'bg-secondary/50' : ''
                          }`}
                        >
                          {columnTasks.map((task, index) => (
                            <Draggable key={task.id} draggableId={task.id} index={index}>
                              {(provided, snapshot) => (
                                <div
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  {...provided.dragHandleProps}
                                  className={`p-3 bg-card border border-border rounded-lg cursor-pointer hover:border-primary/50 transition-all border-l-4 ${PRIORITY_BORDER[task.priority] || 'border-l-zinc-500'} ${
                                    snapshot.isDragging ? 'shadow-lg ring-2 ring-primary/20' : ''
                                  }`}
                                  onClick={() => handleTaskClick(task)}
                                  data-testid={`kanban-task-${task.id}`}
                                >
                                  <p className="text-sm font-medium line-clamp-2 mb-2">
                                    {task.info || "Task senza descrizione"}
                                  </p>
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <Badge variant="outline" className="text-xs font-mono">
                                      {task.system}
                                    </Badge>
                                    <span className="text-xs text-muted-foreground">{task.country}</span>
                                  </div>
                                  {task.completion > 0 && task.completion < 1 && (
                                    <div className="mt-2 flex items-center gap-2">
                                      <Progress value={task.completion * 100} className="h-1.5 flex-1" />
                                      <span className="text-xs text-muted-foreground">
                                        {Math.round(task.completion * 100)}%
                                      </span>
                                    </div>
                                  )}
                                  {task.deadline && (
                                    <p className="text-xs text-muted-foreground mt-2">
                                      {new Date(task.deadline).toLocaleDateString('it-IT')}
                                    </p>
                                  )}
                                </div>
                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                          
                          <Button
                            variant="ghost"
                            className="w-full h-10 border border-dashed border-border hover:border-primary/50 hover:bg-secondary/50"
                            onClick={() => handleCreate(column.id)}
                            data-testid={`add-task-${column.id}`}
                          >
                            <Plus className="w-4 h-4 mr-1" />
                            Aggiungi
                          </Button>
                        </div>
                      )}
                    </Droppable>
                  </CardContent>
                </Card>
              </div>
            );
          })}
        </div>

        {/* Mobile: Vertical stacked columns */}
        <div className="md:hidden space-y-4 flex-1 overflow-y-auto">
          {COLUMNS.map((column) => {
            const columnTasks = getColumnTasks(column.id);
            const Icon = column.icon;

            return (
              <div 
                key={column.id}
                data-testid={`kanban-column-mobile-${column.id}`}
              >
                <Card className="border-border">
                  <CardHeader className="p-4 pb-2">
                    <CardTitle className="text-sm flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Icon className={`w-4 h-4 ${column.color}`} />
                        <span>{column.title}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="secondary" className="text-xs">
                          {columnTasks.length}
                        </Badge>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() => handleCreate(column.id)}
                        >
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-2 pt-0">
                    <Droppable droppableId={column.id}>
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                          className={`min-h-[80px] space-y-2 p-1 rounded-lg transition-colors ${
                            snapshot.isDraggingOver ? 'bg-secondary/50' : ''
                          }`}
                        >
                          {columnTasks.length === 0 ? (
                            <p className="text-xs text-muted-foreground text-center py-4">
                              Nessun task
                            </p>
                          ) : (
                            columnTasks.slice(0, 3).map((task, index) => (
                              <Draggable key={task.id} draggableId={task.id} index={index}>
                                {(provided, snapshot) => (
                                  <div
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    {...provided.dragHandleProps}
                                    className={`p-3 bg-card border border-border rounded-lg cursor-pointer active:scale-[0.98] transition-all border-l-4 ${PRIORITY_BORDER[task.priority] || 'border-l-zinc-500'} ${
                                      snapshot.isDragging ? 'shadow-lg ring-2 ring-primary/20' : ''
                                    }`}
                                    onClick={() => handleTaskClick(task)}
                                  >
                                    <p className="text-sm font-medium line-clamp-1 mb-1">
                                      {task.info || "Task senza descrizione"}
                                    </p>
                                    <div className="flex items-center justify-between">
                                      <div className="flex items-center gap-2">
                                        <Badge variant="outline" className="text-[10px] font-mono">
                                          {task.system}
                                        </Badge>
                                        <span className="text-[10px] text-muted-foreground">{task.country}</span>
                                      </div>
                                      {task.completion > 0 && task.completion < 1 && (
                                        <span className="text-[10px] text-muted-foreground">
                                          {Math.round(task.completion * 100)}%
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                )}
                              </Draggable>
                            ))
                          )}
                          {columnTasks.length > 3 && (
                            <p className="text-xs text-muted-foreground text-center py-1">
                              +{columnTasks.length - 3} altri task
                            </p>
                          )}
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </CardContent>
                </Card>
              </div>
            );
          })}
        </div>
      </DragDropContext>

      <TaskFormModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSave={handleSave}
        task={editingTask}
        options={options}
      />
    </div>
  );
}
